import isNull from 'lodash-es/isNull';
import isUndefined from 'lodash-es/isUndefined';
import { roundAccurately } from './helpers';
import { hoursToMinutes } from './time';
import { EstimateOptionConfidence } from '@/api';

export function estimateString(
  estimate: number,
  options?: {
    showUnitOfTime?: boolean;
    minutesIfLessThanHour?: boolean;
    spaceBetweenNumberAndUnitOfTime?: boolean;
  }
): string {
  let number = estimate;
  let appendString = options?.showUnitOfTime ? 'hrs' : '';
  if (number < 1 && options?.minutesIfLessThanHour) {
    const { minutes } = hoursToMinutes(number);
    number = minutes;
    if (options?.showUnitOfTime) {
      appendString = 'min';
    }
  } else {
    number = roundAccurately(number, 1);
  }
  return (
    number.toLocaleString() +
    (options?.spaceBetweenNumberAndUnitOfTime ? ' ' : '') +
    appendString
  );
}

export function calculatedEstimate(
  optimisticEstimate: number,
  likelyEstimate: number,
  pessimisticEstimate: number
): number {
  return (optimisticEstimate + 4 * likelyEstimate + pessimisticEstimate) / 6;
}

export function calculatedEstimateSd(
  optimisticEstimate: number,
  pessimisticEstimate: number,
  confidenceLevel: EstimateOptionConfidence
) {
  let multiplier: 1 | 2 | 3;
  switch (confidenceLevel) {
    case EstimateOptionConfidence._68:
      multiplier = 1;
      break;
    case EstimateOptionConfidence._95:
      multiplier = 2;
      break;
    case EstimateOptionConfidence._99:
      multiplier = 3;
      break;
    default:
      multiplier = 1;
  }
  return multiplier * ((pessimisticEstimate - optimisticEstimate) / 6);
}

export enum EstimationFieldNames {
  optimisticEstimate = 'optimisticEstimate',
  likelyEstimate = 'likelyEstimate',
  pessimisticEstimate = 'pessimisticEstimate',
}

export type EstimationValidation = {
  isValid: boolean;
  invalidFields: Array<EstimationFieldNames>;
};

function isNullOrUndefined(value: unknown): value is null | undefined {
  return isNull(value) || isUndefined(value);
}

export function validateFields({
  optimisticEstimate,
  likelyEstimate,
  pessimisticEstimate,
  allowZero = false,
}: {
  optimisticEstimate: number;
  likelyEstimate: number;
  pessimisticEstimate: number;
  allowZero?: boolean;
}): EstimationValidation {
  let hasBlankValues =
    !optimisticEstimate || !likelyEstimate || !pessimisticEstimate;
  if (allowZero) {
    hasBlankValues =
      isNullOrUndefined(optimisticEstimate) ||
      isNullOrUndefined(likelyEstimate) ||
      isNullOrUndefined(pessimisticEstimate);
  }
  if (hasBlankValues) {
    // When allowZero is false, mark fields with 0 or falsy values as invalid
    // When allowZero is true, only mark null/undefined fields as invalid
    const isFieldInvalid = allowZero
      ? isNullOrUndefined
      : (value: number) => !value;

    return {
      isValid: false,
      invalidFields: [
        ...(isFieldInvalid(optimisticEstimate)
          ? [EstimationFieldNames.optimisticEstimate]
          : []),
        ...(isFieldInvalid(likelyEstimate)
          ? [EstimationFieldNames.likelyEstimate]
          : []),
        ...(isFieldInvalid(pessimisticEstimate)
          ? [EstimationFieldNames.pessimisticEstimate]
          : []),
      ],
    };
  }

  if (optimisticEstimate < 0 || likelyEstimate < 0 || pessimisticEstimate < 0) {
    return {
      isValid: false,
      invalidFields: [
        ...(optimisticEstimate < 0
          ? [EstimationFieldNames.optimisticEstimate]
          : []),
        ...(likelyEstimate < 0 ? [EstimationFieldNames.likelyEstimate] : []),
        ...(pessimisticEstimate < 0
          ? [EstimationFieldNames.pessimisticEstimate]
          : []),
      ],
    };
  }

  if (
    optimisticEstimate > likelyEstimate ||
    optimisticEstimate > pessimisticEstimate ||
    likelyEstimate > pessimisticEstimate
  ) {
    return {
      isValid: false,
      invalidFields: [
        ...(optimisticEstimate > likelyEstimate
          ? [EstimationFieldNames.optimisticEstimate]
          : []),
        ...(optimisticEstimate > pessimisticEstimate
          ? [EstimationFieldNames.optimisticEstimate]
          : []),
        ...(likelyEstimate > pessimisticEstimate
          ? [EstimationFieldNames.likelyEstimate]
          : []),
      ],
    };
  }

  return {
    isValid: true,
    invalidFields: [],
  };
}
